import type { ReactNode } from 'react';
import { BrandMark } from './ui/primitives';

/**
 * The page's masthead: a band in the brand's own colors, with the mark lapping
 * over its bottom edge.
 *
 * The band is the one place the page shows the brand rather than describing it.
 * Everything below is app chrome in app colors — which is correct, since a
 * guideline page has to stay readable for brands whose palette is three shades
 * of off-white — but a brand kit that opens in no color at all reads as a file
 * list, and that is exactly what this page is replacing.
 *
 * It takes the WHOLE palette, one gradient stop per color. Picking two of them
 * was tried three ways and each rule was wrong on some real kit; a band that
 * shows every color cannot pick the wrong one, and a brand kit is the one page
 * where showing the whole palette is the point.
 *
 * `colors` may be empty: the band falls back to the app's own accent, so a
 * brand with no palette still gets a masthead instead of a hole where one
 * should be.
 */
export function BrandHero({
	name,
	logoUrl,
	colors,
	meta,
	badges,
	actions,
}: {
	name: string;
	/** A rendered thumbnail of the brand's logo, when there is one. */
	logoUrl?: string | null;
	/** The palette, in display order — every color becomes a stop. */
	colors?: readonly string[] | null;
	/** The line under the name — category, client, last updated. */
	meta?: ReactNode;
	/** Status marks beside the name. */
	badges?: ReactNode;
	actions?: ReactNode;
}) {
	/* One stop per color, spread evenly across the band. A single color needs a
	   second stop of its own to be a gradient at all, and no colors at all
	   falls back to the app accent. */
	const stops = colors && colors.length > 0 ? colors : ['var(--accent)', 'var(--accent-hover)'];
	const band =
		stops.length === 1
			? `linear-gradient(120deg, ${stops[0]} 0%, ${stops[0]} 100%)`
			: `linear-gradient(120deg, ${stops
					.map((hex, i) => `${hex} ${Math.round((i / (stops.length - 1)) * 100)}%`)
					.join(', ')})`;

	return (
		<div className="mb-8">
			<div
				className="relative h-[9.5rem] overflow-hidden rounded-[var(--r-4)] border border-line-1"
				style={{ background: band }}
			>
				{/* A dot field, so a flat single-color band still reads as a
				    surface rather than a rendering error. */}
				<div
					className="absolute inset-0"
					style={{
						backgroundImage:
							'radial-gradient(rgba(255,255,255,0.07) 0.5px, transparent 0.5px)',
						backgroundSize: '18px 18px',
					}}
				/>
			</div>

			<div className="relative z-[1] flex flex-wrap items-end gap-x-5 gap-y-3 px-6">
				<div className="-mt-11 self-start rounded-[var(--r-5)] bg-canvas p-1.5">
					<BrandMark name={name} hex={stops[0]} src={logoUrl} size={76} />
				</div>

				<div className="min-w-[16rem] flex-1 pb-1">
					<div className="mb-1 flex flex-wrap items-center gap-2.5">
						<h1 className="text-xl font-medium text-text-1">{name}</h1>
						{badges}
					</div>
					{meta && <div className="text-xs text-text-3">{meta}</div>}
				</div>

				{actions && (
					<div className="ml-auto flex shrink-0 flex-wrap items-center gap-2 pb-1.5">
						{actions}
					</div>
				)}
			</div>
		</div>
	);
}
